import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import {
  required,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { ContentAsset } from "./ContentLibrary";
import { MARKETING_PATHS, sequencePath } from "./paths";
import {
  ANY_TRACK_LABEL,
  SEQUENCE_CHANNELS,
  SEQUENCE_TRACK_CHOICES,
  channelLabel,
  emptyTrackToNull,
  nextStepNo,
  numberingGap,
  planRemoval,
  stepSchedule,
  trackLabel,
} from "./sequenceRules";
import type { Sequence, SequenceStep } from "./types";

/**
 * Sequences — authoring the cadence the nightly job runs.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * `sequences`, `sequence_steps` and `enrollments` have existed since migration
 * 052 and hold ZERO rows between them, not because nobody wanted a nurture
 * cadence but because there has never been a way to write one. A schema with
 * no authoring surface is a plan, not a feature — the same gap the lifecycle
 * had before LifecyclePanel, where a stage nobody could set was a column
 * rather than a process.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO
 * -----------------------------------------
 * It does not enroll anybody. Nothing auto-enrolls in this system and 052 says
 * why: auto-enrollment is how one person lands in two sequences at once, which
 * is the failure Dittofeed's four-part re-entry rule exists to prevent, and
 * not doing it earns the same protection for free. Enrollment is its own
 * screen; this one only decides what a cadence IS.
 *
 * WHY THERE IS NO DELETE, ANYWHERE
 * --------------------------------
 * `enrollments.sequence_id` references `sequences` with no ON DELETE clause,
 * so deleting a used sequence fails outright, and deleting an unused one
 * destroys the record of what somebody was enrolled in the moment it stops
 * being unused. `is_active` is the whole retirement path — the same rule
 * content_assets states on its own screen, and the reason `actions={<></>}`
 * appears below: upstream's Edit view renders a DeleteButton by default,
 * quietly, exactly as DataTable's bulk actions once offered to delete doors.
 *
 * WHY THERE IS NO DRAG TO REORDER
 * -------------------------------
 * `offset_days` is measured from the PREVIOUS step. Dragging step 3 above
 * step 2 would therefore change what BOTH steps mean — a handle would imply
 * a reordering that the data model does not have. Removing a step says out
 * loud what it does to the schedule instead; see ./sequenceRules.ts.
 */

/* ── the list ──────────────────────────────────────────────────────────── */

/**
 * Enough steps for any cadence anyone will write by hand, in one page.
 *
 * The list needs a step count per sequence, and a per-row count query would
 * be one request per row. At the volume this system works at — a handful of
 * sequences, a few steps each — one read of the whole table is cheaper than
 * the round trips, and the number shown is then computed from rows we hold
 * rather than from a count that could disagree with them.
 */
const ALL_STEPS = 1000;

const useAllSteps = () =>
  useGetList<SequenceStep>("sequence_steps", {
    pagination: { page: 1, perPage: ALL_STEPS },
    sort: { field: "step_no", order: "ASC" },
    filter: {},
  });

export const SequenceList = () => {
  const {
    data: sequences,
    isPending,
    error,
  } = useGetList<Sequence>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
    filter: {},
  });
  const { data: steps } = useAllSteps();

  const stepCount = (sequenceId: number) =>
    (steps ?? []).filter((s) => s.sequence_id === sequenceId).length;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="text-lg font-semibold">Sequences</h1>
        {/*
          The Resource's own create route. It is not under /contacts, so the
          sub-nav is not above it -- accepted, because a create form is a
          modal-shaped detour that returns here, and giving it a second custom
          route would mean two URLs rendering the same form.
        */}
        <Button asChild size="sm" variant="outline">
          <Link to="/sequences/create">New sequence</Link>
        </Button>
      </div>

      <p className="mt-1 mb-4 max-w-3xl text-sm text-muted-foreground">
        A named cadence of follow-up steps — day 0 intro, day 3 call, day 7
        check-in. The nightly job turns each due step into a task for somebody
        to do; it never sends anything itself, because sending on a timer is how
        a warm relationship gets three emails at 2:15pm on three consecutive
        Wednesdays.
      </p>

      {/*
        AN ERROR MUST NOT LOOK LIKE AN EMPTY LIST. These tables really are
        empty, so "there are none" is the sentence this screen expects to
        print -- and it is exactly the sentence a denied read would print too.
        The error branch comes first and says what it does not know.
      */}
      {error ? (
        <p className="text-sm text-destructive">
          Could not load the sequences, so this is not a report that there are
          none — it is a report that we do not know.
        </p>
      ) : isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !sequences?.length ? (
        <div className="max-w-2xl space-y-2 text-sm">
          <p>
            No sequences yet. These tables have been empty since they were
            created, because until now there was no way to write one.
          </p>
          <p className="text-muted-foreground">
            Start with a name and a track, then add the steps: each one is a
            channel, an optional template, how long to wait after the previous
            step, and the type of task it should create.
          </p>
        </div>
      ) : (
        <ul className="divide-y rounded-md border">
          {sequences.map((seq) => (
            <li
              key={seq.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-sm"
            >
              <Link
                to={sequencePath(seq.id)}
                className="font-medium underline-offset-2 hover:underline"
              >
                {seq.name}
              </Link>

              <Badge variant="outline" className="text-[0.7rem] font-normal">
                {trackLabel(seq.track)}
              </Badge>

              <Badge variant={seq.is_active ? "default" : "outline"}>
                {seq.is_active ? "active" : "retired"}
              </Badge>

              <span className="ml-auto text-xs text-muted-foreground">
                {/*
                  A sequence with no steps is a real state and a useless one.
                  It used to be worse than useless: the generator looked for
                  step current_step + 1, found nothing, and marked the
                  enrollment completed on the spot -- a record claiming somebody
                  finished a cadence they never received. 077 closed that by
                  refusing an empty plan outright, so the cost today is a
                  refusal at the point of enrolling rather than a lie
                  afterwards. Still worth flagging here, where it can be fixed:
                  a bare "0 steps" reads as a number rather than as a warning.
                */}
                {stepCount(seq.id) === 0 ? (
                  <span className="text-destructive">
                    No steps — nobody can be enrolled on this until it has one
                  </span>
                ) : stepCount(seq.id) === 1 ? (
                  "1 step"
                ) : (
                  `${stepCount(seq.id)} steps`
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ── the form, shared by create and the header of the detail page ──────── */

const SequenceForm = () => (
  <SimpleForm>
    <TextInput source="name" label="Name" validate={required()} />
    <SelectInput
      source="track"
      label="Track"
      choices={SEQUENCE_TRACK_CHOICES}
      emptyText={ANY_TRACK_LABEL}
      /*
        WITHOUT THIS PARSE the empty selection is saved as the empty string,
        `sequences_track_check` rejects it, and not picking a track — a
        perfectly ordinary thing to want — fails with a constraint error.
        SelectInput cannot be told to use null directly; its own source says
        "use parse to turn the empty string into null".
      */
      parse={emptyTrackToNull}
      helperText="Leave it on 'Any track' if the cadence suits prospects and partners alike. That is a real answer, not an unset field."
    />
    <BooleanInput
      source="is_active"
      label="Active"
      defaultValue={true}
      helperText="Turn off to retire this cadence. It stops being offered for new enrollments and stays readable wherever it was already used — there is no delete, because enrollments reference it and deleting one destroys the record of what somebody was enrolled in."
    />
  </SimpleForm>
);

/**
 * Saving a new sequence lands on the sequence itself, not on the list.
 *
 * A sequence with no steps does nothing, so the list is the wrong place to
 * be put down: the next thing the author has to do is add step 1, and that
 * only exists on the detail page. `redirect="list"` would also mean the
 * Resource's own /sequences route, which renders the same list with no
 * Contacts sub-nav above it — the round trip that ContentLibraryCreate
 * documents having got wrong.
 */
export const SequenceCreate = () => (
  <Create
    resource="sequences"
    redirect={(_resource?: string, id?: string | number) =>
      id != null ? sequencePath(id) : MARKETING_PATHS.sequences
    }
  >
    <SequenceForm />
  </Create>
);

/* ── the detail page ───────────────────────────────────────────────────── */

/** Every step of one sequence, in order. */
const useSteps = (sequenceId: number) =>
  useGetList<SequenceStep>("sequence_steps", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "step_no", order: "ASC" },
    filter: { sequence_id: sequenceId },
  });

/**
 * The templates a step may point at.
 *
 * ACTIVE ONLY, matching the library's own default filter: a retired template
 * is one somebody deliberately stopped sending, and offering it for a NEW
 * step would undo that decision silently. Steps already pointing at a retired
 * row keep doing so and still render its name — that history is the reason
 * retirement exists instead of deletion.
 */
const useTemplates = () =>
  useGetList<ContentAsset>("content_assets", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
    filter: { is_active: true },
  });

/**
 * Radix's Select refuses an empty string as an item value, so "no template"
 * needs a sentinel that is not a content id. It never reaches the database:
 * addStep turns it back into NULL, which is what the column holds for a call
 * step or anything else that needs no body written in advance.
 */
const NO_TEMPLATE = "__none__";

export const SequenceDetail = () => {
  const { id: routeId } = useParams();
  const sequenceId = Number(routeId);

  const {
    data: sequence,
    isPending,
    error,
  } = useGetOne<Sequence>("sequences", { id: sequenceId });

  const { data: steps, error: stepsError, refetch } = useSteps(sequenceId);

  if (error) {
    return (
      <div>
        <BackLink />
        <p className="mt-3 text-sm text-destructive">
          Could not load this sequence, so this is not a report that it is gone
          — it is a report that we do not know.
        </p>
      </div>
    );
  }

  if (isPending || !sequence) {
    return (
      <div>
        <BackLink />
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div>
      <BackLink />
      <h1 className="mt-2 text-lg font-semibold">{sequence.name}</h1>

      {/*
        actions={<></>} — the upstream Edit default renders a DeleteButton
        unconditionally, which is exactly the retire-by-delete path this
        resource must not offer. redirect={false} keeps the author on the page
        they are editing rather than throwing them at /sequences, a third URL
        rendering the list again without the Contacts sub-nav.
      */}
      <Edit
        resource="sequences"
        id={sequenceId}
        actions={<></>}
        title={false}
        disableBreadcrumb
        redirect={false}
        mutationMode="pessimistic"
      >
        <SequenceForm />
      </Edit>

      <h2 className="mt-6 text-base font-semibold">Steps</h2>

      <NumberingWarning steps={steps} />

      {stepsError ? (
        <p className="mt-2 text-sm text-destructive">
          Could not read this sequence&apos;s steps, so this is not a report
          that it has none — it is a report that we do not know.
        </p>
      ) : !steps?.length ? (
        <p className="mt-2 max-w-2xl text-sm">
          <span className="text-destructive">This sequence has no steps</span>,
          so enrolling somebody in it does nothing at all: the nightly job looks
          for step 1, finds nothing, and the enrollment completes immediately.
          Add the first step below.
        </p>
      ) : (
        <StepList steps={steps} onChanged={() => refetch()} />
      )}

      <AddStep
        sequenceId={sequenceId}
        steps={steps ?? []}
        onAdded={() => refetch()}
      />
    </div>
  );
};

const BackLink = () => (
  <Link
    to={MARKETING_PATHS.sequences}
    className="text-xs text-muted-foreground underline-offset-2 hover:underline"
  >
    ← All sequences
  </Link>
);

/**
 * The state this screen can no longer create, and that a hand-run DELETE can.
 *
 * advance_enrollments() only ever asks for `step_no = current_step + 1`, so a
 * hole in the numbering does not produce an error — it marks every enrollment
 * that reaches it `completed` with `ended_reason = 'last_step'`, and every
 * step past the hole becomes unreachable. The record then claims the person
 * finished a cadence they were dropped halfway out of, which is the same
 * defect as a lead that just went quiet, wearing the constraint that was
 * meant to prevent it.
 */
const NumberingWarning = ({ steps }: { steps?: SequenceStep[] }) => {
  const gap = steps ? numberingGap(steps) : null;
  if (gap == null) return null;
  return (
    <p className="mt-2 max-w-2xl rounded-md border border-destructive/50 p-2 text-sm text-destructive">
      Step {gap} is missing. Enrollments stop there: the nightly job finds no
      step {gap}, marks the enrollment completed with the reason{" "}
      <code className="text-xs">last_step</code>, and every step after it never
      runs. Removing a step from this screen renumbers the rest, so this gap
      came from somewhere else — add a step {gap} back, or rebuild the sequence.
    </p>
  );
};

const StepList = ({
  steps,
  onChanged,
}: {
  steps: SequenceStep[];
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data: templates } = useTemplates();
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const scheduled = stepSchedule(steps);

  /**
   * Remove a step, and repair the two things a plain delete would break.
   *
   * The order is the contract, not a preference — see planRemoval(). The
   * delete frees the removed number first; the renumbering then runs in
   * ASCENDING order so each write targets a number the row below it has
   * already vacated. Descending would collide on UNIQUE (sequence_id,
   * step_no) halfway through and leave the sequence gapped, with no
   * transaction to undo it.
   *
   * Sequential awaits rather than Promise.all, for the same reason: parallel
   * writes have no order at all.
   */
  const remove = async (stepId: number) => {
    const plan = planRemoval(steps, stepId);
    // Null when the id is gone — somebody else removed it between the render
    // and the click. Doing nothing is right; deleting whatever now sits at
    // that position is not.
    if (!plan) return;

    setBusy(true);
    try {
      await dataProvider.delete("sequence_steps", {
        id: plan.remove.id,
        previousData: plan.remove,
      });
      for (const write of plan.renumber) {
        await dataProvider.update("sequence_steps", {
          id: write.id,
          data: { step_no: write.step_no, offset_days: write.offset_days },
          previousData: steps.find((s) => s.id === write.id),
        });
      }
      setConfirming(null);
      onChanged();
    } catch (e) {
      /*
       * A half-applied removal is the gapped state, so say which step it
       * stopped on rather than "something went wrong". The reader has to be
       * able to find the sequence again and finish the job by hand.
       */
      notify(
        `Step ${plan.remove.step_no} could not be removed cleanly (${
          e instanceof Error ? e.message : "unknown error"
        }). Check the numbering before enrolling anybody.`,
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <ol className="mt-2 divide-y rounded-md border">
      {scheduled.map((step) => {
        const template = templates?.find((t) => t.id === step.content_id);
        const plan = planRemoval(steps, step.id);

        return (
          <li key={step.id} className="px-3 py-2 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-medium">Step {step.step_no}</span>

              {/* The number the author actually wants, derived rather than
                  stored: the column holds the wait, not the day. */}
              <Badge variant="secondary" className="font-normal">
                day {step.day}
              </Badge>

              {/* And the stored number beside it, so the sum above can be
                  checked instead of trusted. */}
              <span className="text-xs text-muted-foreground">
                {waitPhrase(step.step_no, step.offset_days)}
              </span>

              <Badge variant="outline" className="text-[0.7rem] font-normal">
                {channelLabel(step.channel)}
              </Badge>

              <Badge variant="outline" className="text-[0.7rem] font-normal">
                {step.task_type}
              </Badge>

              {template ? (
                <Link
                  to={`/content_assets/${template.id}`}
                  className="text-xs underline-offset-2 hover:underline"
                >
                  {template.name}
                </Link>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {/*
                    A step with no template is legitimate -- a call needs no
                    body written in advance, and advance_enrollments() falls
                    back to the task type for the task's label. But if one was
                    meant to be here, the fix is somewhere else, so link to it.
                  */}
                  No template —{" "}
                  <Link
                    to={MARKETING_PATHS.contactContent}
                    className="underline-offset-2 hover:underline"
                  >
                    content library
                  </Link>
                </span>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                aria-label={`Remove step ${step.step_no}`}
                disabled={busy}
                onClick={() =>
                  setConfirming(confirming === step.id ? null : step.id)
                }
              >
                Remove
              </Button>
            </div>

            {/*
              THE CONSEQUENCE, BEFORE THE CLICK THAT CAUSES IT.
              Both effects of a removal are invisible afterwards: the
              renumbering leaves no trace, and the absorbed wait looks like a
              number somebody typed. Stating them at the moment of removal is
              the only point at which the reader can disagree.
            */}
            {confirming === step.id && plan ? (
              <div className="mt-2 rounded-md border p-2 text-xs">
                <p>
                  {plan.absorbedBy
                    ? `Step ${plan.absorbedBy.step_no} becomes step ${step.step_no}, and its wait grows by ${dayCount(
                        plan.absorbedDays,
                      )} so every step after this one stays on the day it is on now.`
                    : "This is the last step, so nothing after it moves."}
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={busy}
                    onClick={() => remove(step.id)}
                  >
                    Remove it and renumber
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirming(null)}
                  >
                    Keep it
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
};

const dayCount = (days: number) => `${days} ${days === 1 ? "day" : "days"}`;

/**
 * What the stored offset means, in words.
 *
 * Always names the step it is measured FROM. "3 days" alone is the ambiguity
 * this whole file exists to remove: it reads as three days from enrollment,
 * which for any step but the first it is not.
 */
const waitPhrase = (stepNo: number, offsetDays: number) => {
  if (stepNo === 1) {
    return offsetDays === 0
      ? "on enrollment"
      : `${dayCount(offsetDays)} after enrollment`;
  }
  return offsetDays === 0
    ? `same day as step ${stepNo - 1}`
    : `${dayCount(offsetDays)} after step ${stepNo - 1}`;
};

const AddStep = ({
  sequenceId,
  steps,
  onAdded,
}: {
  sequenceId: number;
  steps: SequenceStep[];
  onAdded: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { taskTypes } = useConfigurationContext();
  const { data: templates } = useTemplates();

  const [offset, setOffset] = useState("0");
  const [channel, setChannel] = useState(SEQUENCE_CHANNELS[0].value);
  const [template, setTemplate] = useState(NO_TEMPLATE);
  /*
   * `follow-up` if the configured list has it, otherwise whatever is first.
   * NOT the column default: sequence_steps.task_type defaults to the string
   * 'Follow-up', with a capital F, which is not one of the configured task
   * types ('follow-up') and so is not a type the app's own pickers know. The
   * column has no CHECK, so nothing in Postgres would object. Sending an
   * explicit value every time is what keeps that default from ever reaching
   * tasks.type.
   */
  const [taskType, setTaskType] = useState(
    taskTypes.find((t) => t.value === "follow-up")?.value ??
      taskTypes[0]?.value ??
      "follow-up",
  );
  const [busy, setBusy] = useState(false);

  const stepNo = nextStepNo(steps);

  const add = async () => {
    const days = Number(offset);
    // `check (offset_days >= 0)` in Postgres. Catching it here means a typo is
    // a sentence rather than a constraint error with a column name in it.
    if (!Number.isFinite(days) || days < 0) {
      notify("A wait has to be zero or more days.", { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      await dataProvider.create("sequence_steps", {
        data: {
          sequence_id: sequenceId,
          step_no: stepNo,
          offset_days: days,
          channel,
          content_id: template === NO_TEMPLATE ? null : Number(template),
          task_type: taskType,
        },
      });
      setOffset("0");
      setTemplate(NO_TEMPLATE);
      onAdded();
    } catch (e) {
      notify(
        `Could not add step ${stepNo} (${
          e instanceof Error ? e.message : "unknown error"
        }).`,
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 max-w-3xl rounded-md border p-3">
      <h3 className="text-sm font-medium">Add step {stepNo}</h3>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="sequence-step-offset">
            {stepNo === 1
              ? "Wait (days after enrollment)"
              : "Wait (days after the previous step)"}
          </Label>
          <Input
            id="sequence-step-offset"
            type="number"
            min={0}
            className="w-56"
            value={offset}
            onChange={(e) => setOffset(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sequence-step-channel">Channel</Label>
          <Select value={channel} onValueChange={setChannel} disabled={busy}>
            <SelectTrigger id="sequence-step-channel" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEQUENCE_CHANNELS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sequence-step-task-type">Task type</Label>
          <Select value={taskType} onValueChange={setTaskType} disabled={busy}>
            <SelectTrigger id="sequence-step-task-type" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/*
                FROM THE CONFIGURED LIST, NEVER TYPED. advance_enrollments()
                inserts this string straight into tasks.type, and that column
                has no CHECK -- so a free-text field here is one typo away
                from a task whose type the app's own task picker cannot even
                offer back, which means editing that task silently rewrites
                its type to something else.
              */}
              {taskTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TemplatePicker
          templates={templates}
          value={template}
          onChange={setTemplate}
          disabled={busy}
        />

        <Button size="sm" onClick={add} disabled={busy}>
          Add step
        </Button>
      </div>

      {/*
        THE NOTE THAT REPLACES A DRAG HANDLE, beside the field that makes it
        true. offset_days is measured from the step before, so moving a step
        changes what two steps mean -- and a handle would say the opposite
        without anybody having to be told.
      */}
      <p className="mt-3 max-w-2xl text-xs text-muted-foreground">
        Steps run in order and each wait is measured from the step before it, so
        there is no drag to reorder: moving a step would change what two steps
        mean, not just where one sits. Remove it and add it again in the place
        you want. The nightly job also jitters each task by up to a day either
        way, deliberately — a cadence that lands on the same weekday for ever
        reads as automation.
      </p>
    </div>
  );
};

/**
 * The template picker, and the link out when there is nothing to pick.
 *
 * THERE ARE ZERO ACTIVE CONTENT ASSETS IN PRODUCTION, so an empty picker is
 * the first thing an author meets, not an edge case. An empty dropdown is a
 * dead end; the blockage is fixed on another screen, so this links to it.
 */
const TemplatePicker = ({
  templates,
  value,
  onChange,
  disabled,
}: {
  templates?: ContentAsset[];
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) => {
  if (!templates?.length) {
    return (
      <p className="max-w-xs text-xs text-muted-foreground">
        No templates to choose from yet. Write one in the{" "}
        <Link
          to={MARKETING_PATHS.contactContent}
          className="underline underline-offset-2"
        >
          content library
        </Link>{" "}
        and it appears here. A step without one still works — the task is
        labeled with its type instead.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="sequence-step-template">Template</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id="sequence-step-template" className="w-52">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_TEMPLATE}>No template</SelectItem>
          {templates.map((t) => (
            <SelectItem key={t.id} value={String(t.id)}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

/**
 * One default export, so CRM.tsx's injection is a single `{...sequenceLibrary}`
 * spread rather than separate named imports — the shape upstream already uses
 * for `companies` and `sales`, and the fork uses for `contentLibrary`.
 *
 * No `edit`: the sequence's own fields are edited on the detail page, under
 * /contacts, where its steps are. A second edit route would be a second URL
 * for the same record showing half of it.
 */
export default {
  list: SequenceList,
  create: SequenceCreate,
};
